import React from 'react';
import { graphql, HeadFC } from 'gatsby';
import Layout from '../components/layout/layout';
import Content from '../components/content/content';
import Container from '../components/container/container';
import { BlogPostData } from 'types/post';
import * as styles from './post.module.scss';
import { GatsbyImage, getImage, getSrc } from 'gatsby-plugin-image';
import Divider from '../components/ui/divider/divider';
import Pagination from '../components/pagination/pagination';
import { Disqus } from 'gatsby-plugin-disqus';

interface PageData {
  markdownRemark: BlogPostData;
}

interface PageProps {
  data: PageData;
  pageContext: {
    isLatestPost: boolean | null;
    nextPost: {
      title: string;
      path: string;
    } | null;
    previousPost: {
      title: string;
      path: string;
    } | null;
  };
}

export default function PostPage({ data, pageContext }: PageProps) {
  const post = data.markdownRemark;
  const { tag, author, title, path, image } = post.frontmatter;
  const imageData = image ? getImage(image) : null;
  const disqusConfig = {
    url: `https://wolfire.com/blog${path}`,
    identifier: path,
    title: title,
  };
  return (
    <Layout>
      <Content>
        {imageData && (
          <div className={styles.imageContainer}>
            <div className={styles.image}>
              {pageContext.isLatestPost && <span className={styles.latestPost}>Latest post</span>}
              <GatsbyImage image={imageData} alt={title} />
            </div>
          </div>
        )}
        <Container isContent>
          <header className={styles.header}>
            <h1>{title}</h1>
            <a href="#comments" className={styles.commentCount}>
              Add comment!
            </a>
          </header>
          <p className={styles.highlight}>{post.frontmatter.date}</p>
          <div className={styles.body} dangerouslySetInnerHTML={{ __html: post.html! }} />
        </Container>
        <Container>
          <Divider className={styles.hr} />
          {(tag || author) && (
            <>
              <p className={styles.author}>
                Posted
                {tag && (
                  <>
                    {' '}
                    in <span className={styles.highlight}>{tag}</span>
                  </>
                )}
                {author && (
                  <>
                    {' '}
                    by <span className={styles.highlight}>{author}</span>
                  </>
                )}
              </p>
              <Divider className={styles.hr} />
            </>
          )}
          <div id="comments" className={styles.comments}>
            <Disqus config={disqusConfig} />
          </div>
          <Divider className={styles.hr} />
          <Pagination
            className={styles.pagination}
            previous={{
              path: pageContext.nextPost?.path || undefined,
              title: pageContext.nextPost?.title || 'Previous post',
              isDisabled: !pageContext.nextPost,
            }}
            next={{
              path: pageContext.previousPost?.path || undefined,
              title: pageContext.previousPost?.title || 'Next post',
              isDisabled: !pageContext.previousPost,
            }}
          />
        </Container>
      </Content>
    </Layout>
  );
}

export const Head: HeadFC<PageData> = ({ data }) => {
  const { title, path, image } = data.markdownRemark.frontmatter;
  const description = data.markdownRemark.excerpt;
  const fullTitle = `${title} | Wolfire Games Blog`;
  const imgUrl = image ? image.publicURL : '/content/missing-post-image.jpg';

  return (
    <>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={`https://www.wolfire.com${path}`} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={imgUrl} />
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:creator" content="@Wolfire" />
      <meta property="twitter:title" content={fullTitle} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={imgUrl} />
    </>
  );
};

export const query = graphql`
  query PostTemplateQuery($slug: String!) {
    markdownRemark(frontmatter: { path: { eq: $slug } }) {
      html
      excerpt
      frontmatter {
        title
        path
        date(formatString: "MMMM DD, YYYY")
        tag
        author
        image {
          publicURL
          childImageSharp {
            gatsbyImageData(layout: FULL_WIDTH, placeholder: BLURRED)
          }
        }
      }
    }
  }
`;
